import React, { useState, useEffect, useMemo } from "react";
import {
  FaTimes,
  FaPaperPlane,
  FaCube,
  FaHashtag,
  FaBuilding,
  FaArrowRight,
  FaExclamationTriangle,
} from "react-icons/fa";
import { CustomDropdown, NumberInput, Textarea } from "../../atoms";
import useDrawerTransition from "../../../Hooks/useDrawerTransition";

/**
 * KOTA GÖNDER — üst firmadan alt firmaya kota paylaşımı.
 *
 * QuotaOperationDrawer (Bedelsiz Yükle / İade Al) ile aynı iskelet ve
 * renk dili; farkı iki taraflı olması: solda gönderen firmanın dağıtılmamış
 * bakiyesi, sağda alt firmanın mevcut kotası gösterilir.
 *
 * BAKİYE KAPISI = TOPLAM (dağıtılmamış havuz + cihazlardaki kota), dağıtılmamış
 * havuz DEĞİL. Havuzu aşan gönderim serbesttir; havuz eksiye düşer ve cihazlar
 * bağlandıkça otomatik dağıtım fazlalığı onlardan geri toplar. Toplamı aşan
 * gönderim ise engellenir — firmada olmayan kotayı göndermek onu yoktan var
 * ederdi. Sunucu aynı kuralı uygular (subfirm-quota-transfer.service).
 *
 * BAYİ (#303): `limitToPool` açıkken Kota Ver'de sınır yalnız HAVUZDUR
 * (makinelerde olmayan kota); havuz eksiye düşmez. `requireLiveSource`
 * açıkken Kota Al'da kaynağın kotalı cihazları canlı okunur — bilgi notu
 * gösterilir, kontrol sunucudadır.
 */
const SubFirmQuotaDrawer = ({
  open,
  onClose,
  onSubmit,
  subFirm: subFirmProp,
  parentFirmName,
  // "toSub" = Kota Ver (üst -> alt), "toParent" = Kota Geri Al (alt -> üst)
  direction = "toSub",
  // Geri al modunda üst sınır ALT firmanın kotasıdır; bu özet oradan gelir.
  subFirmQuota = [],
  // [{ productName, available, deviceQuota, total }] — gönderen firmanın özeti
  sendableQuota = [],
  summaryLoading = false,
  isLoading = false,
  limitToPool = false,
  requireLiveSource = false,
}) => {
  const { mounted, entered, data: subFirm } = useDrawerTransition(open, subFirmProp);

  // Yöne göre kaynak (kotayı veren) ve hedef (alan) firma adları.
  const toSub = direction !== "toParent";
  const sourceName = toSub ? parentFirmName : subFirm?.firmName;
  const targetName = toSub ? subFirm?.firmName : parentFirmName;
  const actionLabel = toSub ? "Kota Ver" : "Kota Geri Al";
  // Gönderilebilir bakiye KAYNAK firmanındır.
  const sourceQuota = toSub ? sendableQuota : subFirmQuota;

  const [productName, setProductName] = useState("");
  const [quota, setQuota] = useState(0);
  const [note, setNote] = useState("");
  const [errors, setErrors] = useState({});

  // Yalnızca gönderen firmada TANIMLI ürünler listelenir. Alt firmada tanımlı
  // olmayan ürüne gönderim sunucuda 400 döner; kullanıcıyı oraya kadar
  // götürmemek için alt firmada da bulunmayanlar işaretlenir.
  const subFirmProducts = useMemo(() => {
    const list = subFirm?.productInfo?.productInfo;
    return Array.isArray(list) ? list : [];
  }, [subFirm]);

  // CustomDropdown seçenek bazında `disabled` DESTEKLEMİYOR — alt firmada
  // tanımlı olmayan ürünler listeden tamamen çıkarılır, sayısı altta not
  // olarak gösterilir. (Aksi halde seçilebilir görünüp sunucudan 400 dönerdi.)
  // Ürün İKİ firmada da tanımlı olmalı — transfer yönü ne olursa olsun.
  // (CustomDropdown seçenek bazında `disabled` desteklemiyor, bu yüzden
  // uygun olmayanlar listeden tamamen çıkarılır.)
  const { productOptions, hiddenProductCount } = useMemo(() => {
    const counterpart = toSub ? subFirmProducts : sendableQuota;
    const eligible = (sourceQuota || []).filter((p) =>
      (counterpart || []).some((cp) => cp.productName === p.productName),
    );
    return {
      productOptions: eligible.map((p) => ({
        id: p.productName,
        value: p.productName,
        label: `${p.productName} (${(
          limitToPool && toSub ? Math.max(0, p.available ?? 0) : p.total ?? 0
        ).toLocaleString("tr-TR")})`,
      })),
      hiddenProductCount: (sourceQuota || []).length - eligible.length,
    };
  }, [sourceQuota, sendableQuota, subFirmProducts, toSub, limitToPool]);

  const selected = useMemo(
    () => (sourceQuota || []).find((p) => p.productName === productName) || null,
    [sourceQuota, productName],
  );

  // `available` = dağıtılmamış havuz (bilgi amaçlı), `sendableTotal` = gerçek
  // üst sınır (havuz + cihazlardaki kota).
  const available = Number(selected?.available) || 0;
  const deviceQuota = Number(selected?.deviceQuota) || 0;
  const totalQuota = Number(selected?.total) || available + deviceQuota;
  const poolOnly = limitToPool && toSub;
  const sendableTotal = poolOnly ? Math.max(0, available) : totalQuota;

  // Hedef firmanın (kotayı ALAN) mevcut havuzu.
  const targetCurrentQuota = useMemo(() => {
    if (!productName) return 0;
    if (!toSub) {
      // Geri al: hedef ÜST firma; havuzu `sendableQuota.available`'dadır.
      const item = (sendableQuota || []).find((p) => p.productName === productName);
      return Number(item?.available) || 0;
    }
    // belowFirms yanıtında `poolQuota` firmanın kendi (dağıtılmamış) havuzu,
    // `quota` ise cihazlara dağıtılmış toplamdır (bkz. firm-base.controller).
    const item = subFirmProducts.find((p) => p.productName === productName);
    return Number(item?.poolQuota) || 0;
  }, [productName, subFirmProducts, sendableQuota, toSub]);

  useEffect(() => {
    if (!open) {
      setProductName("");
      setQuota(0);
      setNote("");
      setErrors({});
    }
  }, [open]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  const validate = () => {
    const tempErrors = {};
    if (!productName) tempErrors.productName = "Lütfen bir ürün seçin.";
    if (quota <= 0) tempErrors.quota = "Kota miktarı 0'dan büyük olmalıdır.";
    else if (quota > sendableTotal) {
      tempErrors.quota = poolOnly
        ? `Havuzda ${sendableTotal.toLocaleString("tr-TR")} kota var — makinelerdeki kota gönderilemez.`
        : `Toplam kota ${sendableTotal.toLocaleString("tr-TR")} — daha fazlası aktarılamaz.`;
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({ subFirmId: subFirm?._id, productName, quota, note, direction });
    }
  };

  return (
    <>
      <div
        className={`fixed inset-y-0 right-0 w-full sm:w-[460px] transform ${
          entered ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 ease-in-out shadow-2xl z-[9999] flex flex-col bg-white`}
      >
        {isLoading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-indigo-500 rounded-full animate-spin" />
            <p className="mt-4 text-sm font-Medium text-gray-700">Aktarılıyor...</p>
            <p className="mt-1 text-xs text-gray-500">
              Kota iki firmanın havuzu arasında aktarılıyor
            </p>
          </div>
        )}

        <div className={`${toSub ? "bg-indigo-50 border-indigo-100" : "bg-amber-50 border-amber-100"} border-b px-5 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`p-2 text-lg rounded-full ${toSub ? "bg-indigo-100 text-indigo-600" : "bg-amber-100 text-amber-700"}`}>
                <FaPaperPlane className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-SemiBold text-gray-800">{actionLabel}</h3>
                <p className="text-xs text-gray-500 truncate">
                  {subFirm?.firmName || "-"}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="p-2 rounded-full hover:bg-gray-200 text-gray-500 disabled:opacity-50 transition-colors"
            >
              <FaTimes className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {/* Transfer yönü — hangi firmadan hangisine */}
            <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-wide text-gray-400">
                  Veren
                </p>
                <p className="text-sm font-SemiBold text-gray-800 truncate">
                  {sourceName || "-"}
                </p>
              </div>
              <FaArrowRight className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
              <div className="flex-1 min-w-0 text-right">
                <p className="text-[10px] uppercase tracking-wide text-gray-400">
                  Alan
                </p>
                <p className="text-sm font-SemiBold text-gray-800 truncate">
                  {targetName || "-"}
                </p>
              </div>
            </div>

            <CustomDropdown
              label={
                <span className="text-base font-Medium text-gray-600 flex items-center gap-2">
                  <FaCube /> Ürün
                </span>
              }
              options={productOptions}
              value={
                productName
                  ? { id: productName, label: productName, value: productName }
                  : null
              }
              onChange={(option) => {
                setProductName(option?.value || "");
                if (errors.productName) {
                  setErrors((p) => ({ ...p, productName: undefined }));
                }
              }}
              placeholder={summaryLoading ? "Yükleniyor..." : "Ürün Seçiniz"}
              searchPlaceholder="Ürün ara..."
              error={!!errors.productName}
              errorMessage={errors.productName}
              required
            />

            {!summaryLoading && productOptions.length === 0 && (
              <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5">
                <FaExclamationTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700">
                  Ortak ürün yok — iki firmada da tanımlı ve aktif bir ürün
                  bulunamadı. Önce ilgili firmaya ürünü ekleyin.
                </p>
              </div>
            )}

            {hiddenProductCount > 0 && productOptions.length > 0 && (
              <p className="text-[11px] text-gray-400 -mt-3">
                {hiddenProductCount} ürün listede yok — karşı firmada tanımlı değil.
              </p>
            )}

            {productName && (
              <div className="rounded-xl border-2 border-indigo-200 bg-indigo-50 p-4">
                <p className="text-xs font-SemiBold uppercase tracking-wide text-gray-500 mb-1">
                  {poolOnly ? "Gönderilebilir (havuz)" : "Aktarılabilir toplam"} — {productName}
                </p>
                <p className="text-2xl sm:text-3xl font-Bold tabular-nums text-gray-800">
                  {sendableTotal.toLocaleString("tr-TR")}
                  <span className="text-base font-Medium text-gray-500 ml-1.5">
                    adet
                  </span>
                </p>
                <div className="mt-3 pt-3 border-t border-gray-200/80 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Dağıtılmamış havuz</span>
                    <span className="font-SemiBold tabular-nums text-gray-600">
                      {available.toLocaleString("tr-TR")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">
                      {sourceName} cihazlarındaki kota
                    </span>
                    <span className="font-SemiBold tabular-nums text-gray-600">
                      {deviceQuota.toLocaleString("tr-TR")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 flex items-center gap-1">
                      <FaBuilding className="w-3 h-3 text-indigo-400" />
                      {targetName} mevcut kotası
                    </span>
                    <span className="font-SemiBold tabular-nums text-indigo-600">
                      {targetCurrentQuota.toLocaleString("tr-TR")}
                    </span>
                  </div>
                  {quota > 0 && quota <= sendableTotal && (
                    <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200/80">
                      <span className="text-gray-600">Transfer sonrası havuz</span>
                      <span className="font-SemiBold tabular-nums text-gray-800">
                        <span
                          className={
                            available - quota < 0 ? "text-red-500" : "text-gray-800"
                          }
                        >
                          {(available - quota).toLocaleString("tr-TR")}
                        </span>
                        <FaArrowRight className="inline w-2.5 h-2.5 mx-1.5 text-gray-400" />
                        <span className="text-emerald-600">
                          {(targetCurrentQuota + quota).toLocaleString("tr-TR")}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
                {/* Havuzu aşan gönderim ENGEL DEĞİL, yalnızca bilgilendirme:
                    havuz eksiye düşer, cihazlar bağlandıkça otomatik dağıtım
                    fazlalığı onlardan geri toplar. */}
                {poolOnly && deviceQuota > 0 && (
                  <p className="mt-3 text-[11px] text-gray-500">
                    {sourceName} makinelerindeki {deviceQuota.toLocaleString("tr-TR")} kota
                    gönderilemez; yalnız havuzdaki kota aktarılır.
                  </p>
                )}
                {requireLiveSource && !toSub && (
                  <div className="mt-3 flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2">
                    <FaExclamationTriangle className="w-3.5 h-3.5 text-blue-500 mt-0.5 shrink-0" />
                    <p className="text-[11px] text-blue-700">
                      Kota almak için {sourceName} firmasının kotalı makineleri
                      bağlı olmalı — makinelerdeki güncel kota okunur, çevrimdışı
                      makine varsa işlem yapılmaz.
                    </p>
                  </div>
                )}
                {quota > available && quota <= sendableTotal && (
                  <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                    <FaExclamationTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                    <p className="text-[11px] text-amber-700">
                      Dağıtılmamış havuz {available.toLocaleString("tr-TR")} —
                      aktarım sonrası bakiye{" "}
                      <span className="font-Bold">
                        {(available - quota).toLocaleString("tr-TR")}
                      </span>{" "}
                      olacak. Cihazlar bağlandıkça fark onlardan geri toplanır.
                    </p>
                  </div>
                )}
              </div>
            )}

            <NumberInput
              label={
                <span className="text-base font-Medium text-gray-600 flex items-center gap-2">
                  <FaHashtag /> Miktar
                </span>
              }
              value={quota}
              onChange={(value) => {
                setQuota(value);
                if (errors.quota) setErrors((p) => ({ ...p, quota: undefined }));
              }}
              placeholder="Miktar girin"
              min={0}
              max={sendableTotal || undefined}
              error={!!errors.quota}
              errorMessage={errors.quota}
              required
            />

            <Textarea
              label={
                <span className="text-base font-Medium text-gray-600 flex items-center gap-2">
                  Açıklama (opsiyonel)
                </span>
              }
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Aktarım nedeni / not..."
              rows={3}
            />
          </div>

          <div className="px-5 py-4 bg-gray-50 border-t border-gray-200 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-600 text-sm font-Medium rounded-xl hover:bg-gray-50"
            >
              <FaTimes className="w-3.5 h-3.5" />
              İptal
            </button>
            <button
              type="submit"
              disabled={isLoading || !productName || sendableTotal <= 0}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-base font-SemiBold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md ${toSub ? "bg-indigo-500 hover:bg-indigo-600" : "bg-amber-500 hover:bg-amber-600"}`}
            >
              {isLoading ? (
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <FaPaperPlane className="w-4 h-4" />
              )}
              {isLoading ? "Aktarılıyor..." : actionLabel}
            </button>
          </div>
        </form>
      </div>

      {mounted && (
        <div
          className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998] transition-opacity duration-300 ease-in-out ${
            entered ? "opacity-100" : "opacity-0"
          }`}
          onClick={isLoading ? undefined : onClose}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default SubFirmQuotaDrawer;
